import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useStandingsSplit } from '@/hooks/useStandingsSplit'
import { useManagerStandings } from '@/hooks/useManagerStandings'
import { useManagerStreaks } from '@/hooks/useManagerForms'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { sortStandings } from '@/lib/standings'
import { useSeason } from '@/context/SeasonContext'
import { Standings } from './components'
import type { StandingsData, ManagerStanding } from './types'

function transformSplitRow(rows: Array<{
  user_id: string
  name: string
  pts: number
  mp: number
  w: number
  d: number
  l: number
  f: number
  rc: number
  dd: number
}>): ManagerStanding[] {
  return rows.map((row, index) => ({
    managerId: row.user_id,
    displayName: row.name,
    rank: index + 1,
    points: row.pts,
    matchesPlayed: row.mp,
    wins: row.w,
    draws: row.d,
    losses: row.l,
    forfeits: row.f,
    redCards: row.rc,
    ddUsed: row.dd,
  }))
}

export function StandingsSection() {
  const { user, currentMembership, loadingMemberships } = useAuth()
  const { season } = useSeason()

  const { seasonSplits, showFormIndicators } = useLeagueConfig()
  const { data: splitData, isLoading: isLoadingSplit, error: splitError } = useStandingsSplit(season)
  const { data: managerStandings, isLoading: isLoadingManager, error: managerError } = useManagerStandings(season)
  const { data: forms } = useManagerStreaks(season, seasonSplits)

  // Transform data to component format
  const standings: StandingsData = useMemo(() => {
    // If split data is available, use it
    if (splitData?.apertura?.length || splitData?.clausura?.length || splitData?.overall?.length) {
      return {
        apertura: transformSplitRow(splitData.apertura || []),
        clausura: transformSplitRow(splitData.clausura || []),
        overall: transformSplitRow(splitData.overall || []),
      }
    }

    // Fallback to manager standings for overall only
    if (managerStandings?.length) {
      const sorted = sortStandings(
        managerStandings.map(ms => ({
          user_id: ms.user_id,
          name: ms.user_name,
          pts: ms.total_points,
          mp: ms.total_picks,
          w: ms.total_wins,
          d: ms.total_draws,
          l: ms.total_losses,
          f: 0,
          rc: ms.red_cards,
          dd: ms.tdd_used,
        }))
      )
      const overall = transformSplitRow(sorted)
      return {
        apertura: [],
        clausura: [],
        overall,
      }
    }

    return { apertura: [], clausura: [], overall: [] }
  }, [splitData, managerStandings])

  // Loading state
  if (!user) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please log in to view standings.</p>
      </div>
    )
  }

  if (loadingMemberships || isLoadingSplit || isLoadingManager) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  if (!currentMembership) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please select a league to view standings.</p>
      </div>
    )
  }

  if (splitError && managerError) {
    return (
      <div className="text-center py-12 text-red-500 dark:text-red-400">
        <p>Failed to load standings. Please try again.</p>
      </div>
    )
  }

  return (
    <Standings
      standings={standings}
      currentUserId={user.id}
      splitNames={[seasonSplits[0]?.name ?? '1H', seasonSplits[1]?.name ?? '2H']}
      forms={showFormIndicators ? forms : undefined}
    />
  )
}
